import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAppDispatch, useAppSelector } from "../../../services/redux/hooks";
import {
  resetAll,
  setSubmitError,
  setSubmitLoading,
  setSubmitSuccess,
  setVerifyFound,
  setVerifyLoading,
  setVerifyNotFound,
} from "../../../services/redux/slices/complaintSlice";
import service from "../../../services/api/service";
import postsServices from "../../../services/api/service/postsService";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import {
  ComplaintPath,
  ComplaintSuccessData,
  FileError,
  NoTicketSubPath,
} from "../complaint.types";
import { ROUTES } from "../../../routes/routerConfig";
import { ALL_TICKET_STATUS_CODES, ApiTicketItem } from "../support.types";
import { mapApiTicket } from "../supportMappers";
import { validateDocumentFile } from "../../../utils/imageValidation";
import { decryptObject, encrypt, encryptObject } from "../../../utils/crypto";
import { formatDateToShortMonth } from "../../../utils/dateFormatUtils";
import { useAuth } from "../../../hooks/useAuth";

const CREATE_TICKET_ENDPOINT = "api/v1/qrc/create-ticket";

// verify-ticket returns a freeform status string; normalise before comparing.
export const isPendingInformation = (status: string): boolean =>
  status.trim().toLowerCase() === "pending information";

const ACCEPTED_MIME = ["application/pdf", "image/png", "image/jpeg"];
// Exported so the dropzone hint on the page quotes the same number this enforces.
export const MAX_TOTAL_SIZE_MB = 10; // total across all files
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

async function validateFiles(
  incoming: File[],
  existing: File[]
): Promise<{ valid: File[]; errors: FileError[] }> {
  const errors: FileError[] = [];
  const valid: File[] = [];

  const existingTotalSize = existing.reduce((sum, f) => sum + f.size, 0);
  let accumulatedSize = existingTotalSize;

  for (const file of incoming) {
    if (!ACCEPTED_MIME.includes(file.type)) {
      errors.push({ name: file.name, reason: "Only PDF, JPG, PNG allowed" });
    } else {
      const contentCheck = await validateDocumentFile(file);
      if (!contentCheck.isValid) {
        errors.push({ name: file.name, reason: "File content does not match its type. Only real PDF, JPG, PNG allowed" });
      } else if (accumulatedSize + file.size > MAX_TOTAL_SIZE_BYTES) {
        errors.push({
          name: file.name,
          reason: `Total upload size exceeds ${MAX_TOTAL_SIZE_MB} MB limit`,
        });
      } else {
        valid.push(file);
        accumulatedSize += file.size;
      }
    }
  }

  return { valid, errors };
}

export interface UseLodgeComplaintReturn {
  selectedPath: ComplaintPath | null;
  noTicketSubPath: NoTicketSubPath | null;
  ticketIdInput: string;
  setTicketIdInput: (val: string) => void;
  verifiedTicketId: string;
  verifiedTicketStatus: string;
  verifyDialogOpen: boolean;
  withTicketFormVisible: boolean;
  successData: ComplaintSuccessData | null;
  attachments: File[];
  fileErrors: FileError[];
  dragOver: boolean;
  setDragOver: (val: boolean) => void;
  verifyStatus: string;
  verifyMessage: string | null;
  submitStatus: string;
  errorMessage: string | null;
  handlePathSelect: (path: ComplaintPath) => void;
  handleSubPathSelect: (sub: NoTicketSubPath) => void;
  handleVerify: () => Promise<void>;
  handleVerifyDialogYes: () => void;
  handleVerifyDialogNo: () => void;
  handleNoTicketSubmit: (loanAccountNumber: string, details: string) => Promise<void>;
  handleWithTicketSubmit: (reason: string, context: string) => Promise<void>;
  handleSuccessClose: () => void;
  handleGoToDashboard: () => void;
  isAuthenticated: boolean;
  addFiles: (files: FileList | null) => void;
  removeFile: (index: number) => void;
}

export function useLodgeComplaint(): UseLodgeComplaintReturn {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();
  const { verifyStatus, submitStatus, errorMessage, verifiedLoanAccountNumber, verifyMessage } = useAppSelector(
    (state) => state.complaint
  );

  const [selectedPath, setSelectedPath] = useState<ComplaintPath | null>(null);
  const [noTicketSubPath, setNoTicketSubPath] = useState<NoTicketSubPath | null>(null);
  const [ticketIdInput, setTicketIdInput] = useState("");
  const [verifiedTicketId, setVerifiedTicketId] = useState("");
  // Status from verify-ticket ("In Progress" / "Pending Information" / ...). It decides which
  // prompt the dialog shows and where "Yes" takes the customer.
  const [verifiedTicketStatus, setVerifiedTicketStatus] = useState("");
  const [verifiedTicketLan, setVerifiedTicketLan] = useState("");
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [withTicketFormVisible, setWithTicketFormVisible] = useState(false);
  const [successData, setSuccessData] = useState<ComplaintSuccessData | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<FileError[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // Same as Raise a Service Request: arriving here is always a fresh start, so a persisted
  // "loading" from a submit that a refresh interrupted would otherwise leave the form stuck in
  // its submitting state with no request left to finish it.
  useEffect(() => {
    dispatch(resetAll());
  }, [dispatch]);

  const handlePathSelect = useCallback(
    (path: ComplaintPath) => {
      // Toggle: clicking the same path collapses it
      if (selectedPath === path) {
        setSelectedPath(null);
      } else {
        setSelectedPath(path);
      }
      // Reset sub-state when switching
      setNoTicketSubPath(null);
      setTicketIdInput("");
      setVerifiedTicketId("");
      setWithTicketFormVisible(false);
      setAttachments([]);
      setFileErrors([]);
      dispatch(resetAll());
    },
    [selectedPath, dispatch]
  );

  const handleSubPathSelect = useCallback((sub: NoTicketSubPath) => {
    setNoTicketSubPath(sub);
    setAttachments([]);
    setFileErrors([]);
  }, []);

  const handleVerify = useCallback(async () => {
    const id = ticketIdInput.trim();
    if (!id) return;
    dispatch(setVerifyLoading());
    try {
      const res = await service.support.verifyTicket(id);
      const verified: boolean = res?.data?.data?.verified ?? false;
      const lan: string = res?.data?.data?.lan ?? "";
      const ticketStatus: string = res?.data?.data?.status ?? "";
      const message: string | undefined = res?.data?.message;
      if (verified) {
        setVerifiedTicketId(id);
        setVerifiedTicketStatus(ticketStatus);
        setVerifiedTicketLan(lan);
        dispatch(setVerifyFound({ ticketId: id, lan }));
        // A Resolved/Closed ticket isn't "being worked on", so skip the "give us more
        // time to resolve this" prompt and go straight to the complaint form.
        const isResolvedOrClosed = ["resolved", "closed"].includes(ticketStatus.trim().toLowerCase());
        if (isResolvedOrClosed) {
          setWithTicketFormVisible(true);
        } else {
          setVerifyDialogOpen(true);
        }
      } else {
        dispatch(setVerifyNotFound(message));
      }
    } catch (error: any) {
      // Show the error message returned by the API (e.g. 404 "ticket not found").
      dispatch(setVerifyNotFound(error?.response?.data?.message));
    }
  }, [ticketIdInput, dispatch]);

  // "Pending Information" means we're waiting on the customer, so "Yes, I'll share the details"
  // has to land them on that ticket. verify-ticket doesn't return the listing's req_id, which
  // Ticket Details needs to load a conversation, so resolve it from the listing first and fall
  // back to the View Tickets section (which the prompt names) if it can't be found.
  const openPendingInformationTicket = useCallback(
    async (ticketId: string, lan: string) => {
      try {
        const listingPayload = lan
          ? { lan: [lan], status: ALL_TICKET_STATUS_CODES }
          : { status: ALL_TICKET_STATUS_CODES };
        const res = await service.support.getTicketListing(
          { data: encryptObject(listingPayload) },
          1,
          100
        );
        const rawData = res?.data?.data;
        const encryptedTickets = typeof rawData === "string" ? rawData : rawData?.data;
        const apiTickets: ApiTicketItem[] = decryptObject(encryptedTickets) ?? [];
        const match = apiTickets.find((t) => String(t.ticketNumber) === String(ticketId));
        if (match) {
          const ticket = mapApiTicket(match);
          navigate(`${ROUTES.ContactUs.ticketDetails.locationPath}/${ticket.ticketNumber}`, {
            state: { ticket, chatMode: !!ticket.loanAccountNumber },
          });
          return;
        }
      } catch {
        // fall through to the listing
      }
      navigate(ROUTES.ContactUs.viewTickets.locationPath);
    },
    [navigate]
  );

  const handleVerifyDialogYes = useCallback(() => {
    setVerifyDialogOpen(false);
    dispatch(resetAll());
    if (isPendingInformation(verifiedTicketStatus)) {
      void openPendingInformationTicket(verifiedTicketId, verifiedTicketLan);
      return;
    }
    // In-Progress: the customer agreed to give us more time — acknowledge it on the dashboard.
    dispatch(
      setStatusPopup({
        status: "success",
        message: "Thank you for allowing us more time.",
      })
    );
    navigate("/dashboard");
  }, [
    dispatch,
    navigate,
    verifiedTicketStatus,
    verifiedTicketId,
    verifiedTicketLan,
    openPendingInformationTicket,
  ]);

  const handleVerifyDialogNo = useCallback(() => {
    setVerifyDialogOpen(false);
    setWithTicketFormVisible(true);
  }, []);

  const handleNoTicketSubmit = useCallback(
    async (loanAccountNumber: string, details: string) => {
      if (!noTicketSubPath) return;
      dispatch(setSubmitLoading());
      // "Lodge a Complaint" sends the text under "complaintDetails"; "Raise a Query / Request"
      // keeps "description".
      const isComplaint = noTicketSubPath === "complaint";
      const detailsFieldName = isComplaint ? "complaintDetails" : "description";
      const formData = new FormData();
      formData.append("key", DOMPurify.sanitize(encrypt(loanAccountNumber)));
      formData.append(detailsFieldName, DOMPurify.sanitize(encrypt(details)));
      formData.append("complaintReason", DOMPurify.sanitize(encrypt("")));
      formData.append("breachedTicketId", DOMPurify.sanitize(encrypt("")));
      formData.append("loginType", DOMPurify.sanitize(encrypt(isAuthenticated ? "postlogin" : "prelogin")));
      attachments.forEach((file) => formData.append("attachments", file));
      try {
        const response = await postsServices.addPost(CREATE_TICKET_ENDPOINT, formData);
        try {
          const decrypted = decryptObject(response?.data?.data) as { id?: number; submissionDate?: string };
          const submittedAt = formatDateToShortMonth(decrypted?.submissionDate ?? new Date().toISOString());
          const ticketId = decrypted?.id ? String(decrypted.id) : "-";
          dispatch(setSubmitSuccess(ticketId));
          setSuccessData({ ticketId, submittedAt });
        } catch {
          const submittedAt = formatDateToShortMonth(new Date().toISOString());
          dispatch(setSubmitSuccess("-"));
          setSuccessData({ ticketId: "-", submittedAt });
        }
      } catch (error: any) {
        // Show the error message returned by the API.
        const errorText = error?.response?.data?.message;
        dispatch(setSubmitError(errorText ?? ""));
        dispatch(setStatusPopup({ status: "error", message: errorText }));
      }
    },
    [noTicketSubPath, attachments, dispatch, isAuthenticated]
  );

  const handleWithTicketSubmit = useCallback(
    async (reason: string, context: string) => {
      dispatch(setSubmitLoading());
      const formData = new FormData();
      formData.append("key", DOMPurify.sanitize(encrypt(verifiedLoanAccountNumber ?? "")));
      // Complaint-against-existing-ticket flow sends the context under "complaintDetails".
      formData.append("complaintDetails", DOMPurify.sanitize(encrypt(context)));
      formData.append("complaintReason", DOMPurify.sanitize(encrypt(reason)));
      formData.append("breachedTicketId", DOMPurify.sanitize(encrypt(verifiedTicketId)));
      formData.append("loginType", DOMPurify.sanitize(encrypt(isAuthenticated ? "postlogin" : "prelogin")));
      attachments.forEach((file) => formData.append("attachments", file));

      try {
        const res = await postsServices.addPost(CREATE_TICKET_ENDPOINT, formData);
        const decrypted = decryptObject(res?.data?.data) as { id?: string | number; submissionDate?: string };
        const ticketId = decrypted?.id != null ? String(decrypted.id) : `CPT${Date.now()}`;
        const submittedAt = formatDateToShortMonth(decrypted?.submissionDate ?? new Date().toISOString());
        dispatch(setSubmitSuccess(ticketId));
        setSuccessData({ ticketId, submittedAt, previousTicketId: verifiedTicketId });
      } catch (error: any) {
        // Show the error message returned by the API.
        dispatch(setSubmitError(error?.response?.data?.message ?? ""));
      }
    },
    [verifiedTicketId, verifiedLoanAccountNumber, attachments, dispatch, isAuthenticated]
  );

  const resetAfterSuccess = useCallback(() => {
    dispatch(resetAll());
    setSuccessData(null);
    setSelectedPath(null);
    setNoTicketSubPath(null);
    setTicketIdInput("");
    setVerifiedTicketId("");
    setWithTicketFormVisible(false);
    setAttachments([]);
  }, [dispatch]);

  const handleSuccessClose = useCallback(() => {
    resetAfterSuccess();
    navigate(ROUTES.ContactUs.viewTickets.locationPath);
  }, [resetAfterSuccess, navigate]);

  const handleGoToDashboard = useCallback(() => {
    resetAfterSuccess();
    navigate(isAuthenticated ? "/dashboard" : ROUTES.HelpCenter.locationPath);
  }, [resetAfterSuccess, navigate, isAuthenticated]);

  const addFiles = useCallback(
    async (incoming: FileList | null) => {
      if (!incoming) return;
      const { valid, errors } = await validateFiles(Array.from(incoming), attachments);
      setFileErrors(errors);
      setAttachments((prev) => [...prev, ...valid]);
    },
    [attachments]
  );

  const removeFile = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setFileErrors([]);
  }, []);

  return {
    selectedPath,
    noTicketSubPath,
    ticketIdInput,
    setTicketIdInput,
    verifiedTicketId,
    verifiedTicketStatus,
    verifyDialogOpen,
    withTicketFormVisible,
    successData,
    attachments,
    fileErrors,
    dragOver,
    setDragOver,
    verifyStatus,
    verifyMessage,
    submitStatus,
    errorMessage,
    handlePathSelect,
    handleSubPathSelect,
    handleVerify,
    handleVerifyDialogYes,
    handleVerifyDialogNo,
    handleNoTicketSubmit,
    handleWithTicketSubmit,
    handleSuccessClose,
    handleGoToDashboard,
    isAuthenticated,
    addFiles,
    removeFile,
  };
}